import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Radio, HelpCircle, Eye, X, ChevronRight } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useStores } from "stores/setup/use-store";
import { StoreState } from "enum/StoreState";
import { SensorAlertSectionProps, SensorAlertState } from "./types";
import { SensorEmptyState } from "./SensorEmptyState";
import { SensorSuggestion } from "./SensorSuggestion";
import { SensorConfiguration } from "./SensorConfiguration";
import { ConfirmationModal } from "./ConfirmationModal";
import { SensorAlertTemplateModal } from "./SensorAlertTemplateModal";
import { HowItWorksExampleMessage } from "./HowItWorksExampleMessage";
import { SensorAlertTabs, coerceSensorAlertTab } from "./SensorAlertTabs";
import type { SensorAlertTabType } from "./SensorAlertTabs";
import { LightningTabContent } from "./lightning/LightningTabContent";
import { SA_COLORS } from "./sensor-alert-colors";
import { useLightningSlice } from "./hooks/useLightningSlice";
import { formatConditions } from "pages/OceanWeatherIntelligence/InsightAlert/util/formatConditionGroups";
import { useSensorSuggestion } from "./hooks/useSensorSuggestion";
import { useSensorAlertSection } from "./hooks/useSensorAlertSection";
import {
  ConditionGroup,
  ISensorAlertConfigLightning,
} from "stores/data/insightAlerts/insightAlertModel";
import { SensorAlertConfig } from "types/ISensorAlertConfig";
import {
  isLightningAllowedForOrigin,
  type SensorSelectionOrigin,
} from "util/lightningGate";

interface SensorAlertSectionContainerProps extends SensorAlertSectionProps {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  sensorConditions: ConditionGroup[];
  alertFrequency: number;
  errors?: any;
  touched?: any;
  /** Existing sensorAlertConfig from form initial values (edit mode only). Used to
   *  transition directly to configuration state and defer the onSelectSensors sync
   *  until sensor data has loaded (FR-027, FR-029). */
  initialSensorAlertConfig?: SensorAlertConfig;
  /** Insight ID of the event being edited. When provided the FC return URL uses
   *  origin=event-edit so the edit drawer is reopened instead of the create drawer. */
  editModeEventId?: number;
  /** Alert ID being edited from Alerts page. When provided the FC return URL uses
   *  origin=alert-edit so the alert edit drawer is reopened. */
  editModeAlertId?: number;
  /** Name of the event being configured — shown in the message preview footer. */
  eventName?: string;
  /** Location area name of the event — shown in the message preview footer. */
  eventLocationName?: string;
}

export const SensorAlertSection: React.FC<SensorAlertSectionContainerProps> =
  observer(
    ({
      locationPoint,
      geofence,
      macroRegionId,
      macroRegionOrigin,
      setFieldValue,
      sensorConditions,
      alertFrequency,
      errors,
      touched,
      initialSensorAlertConfig,
      editModeEventId,
      editModeAlertId,
      eventName,
      eventLocationName,
    }) => {
      const {
        dataStores: { insightAlertStore, forecastChartPageStore },
      } = useStores();

      const history = useHistory();
      const location = useLocation();

      const { t, i18n } = useTranslation("translation", {
        keyPrefix: "oceanWeatherIntelligence.sensorAlert",
      });

      const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
      const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
      const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
      // Spec 043 — per-tab remove. The X on each tab opens a confirmation modal
      // scoped to that single sub-block (rain or lightning) — distinct from the
      // header "Desativar alerta" which kills the whole sensor alert.
      const [removeTarget, setRemoveTarget] =
        useState<SensorAlertTabType | null>(null);

      // Spec 043 — lightning slice. Hydrated once from initialSensorAlertConfig
      // (edit mode); kept in sync with formik via setFieldValue below.
      const lightning = useLightningSlice();
      const [hasHydratedLightning, setHasHydratedLightning] = useState(false);
      const [activeTab, setActiveTab] = useState<SensorAlertTabType>("rain");

      // Determine origin based on current page (same pattern as useAlertGeospatial).
      // In edit mode, use "event-edit" or "alert-edit" so SensorSelectionControls
      // can navigate back to reopen the correct drawer.
      const sensorSelectionOrigin: SensorSelectionOrigin = (() => {
        if (editModeAlertId) return "alert-edit";
        if (editModeEventId) return "event-edit";
        if (location.pathname === "/insight-manager") return "insight-manager";
        if (location.pathname === "/insight-alerts") return "insight-alerts";
        return "forecast-charts";
      })();

      // Gate: criação de evento via insight-manager não expõe a opção de raio.
      // Demais fluxos (alertas, edição de evento, forecast-charts) seguem normais.
      const lightningAllowed = isLightningAllowedForOrigin(sensorSelectionOrigin);
      const hiddenSensorTabs: SensorAlertTabType[] = lightningAllowed
        ? []
        : ["lightning"];

      const persistLightningSlice = useCallback(
        (next: ISensorAlertConfigLightning | null) => {
          // Merge into the existing sensorAlertConfig form value so the rain
          // slice (sensors/conditions/alertFrequency) is preserved.
          const current =
            (initialSensorAlertConfig as SensorAlertConfig | undefined) ?? {
              sensors: [],
              conditions: [],
            };
          const merged: SensorAlertConfig = {
            ...current,
            sensors: current.sensors ?? [],
            conditions: current.conditions ?? [],
            lightning: next ?? undefined,
          };
          setFieldValue("sensorAlertConfig", merged);
        },
        [initialSensorAlertConfig, setFieldValue],
      );

      useEffect(() => {
        if (hasHydratedLightning) return;
        // Gate: quando lightning está bloqueado pela origem (criação via
        // insight-manager), descarta qualquer slice residual em vez de
        // hidratar — protege o payload do form contra estado vazado da
        // store ou do `initialSensorAlertConfig`.
        if (!lightningAllowed) {
          lightning.clear();
          insightAlertStore.setLightningSelection(null);
          if (initialSensorAlertConfig?.lightning) {
            persistLightningSlice(null);
          }
          setHasHydratedLightning(true);
          return;
        }
        // Priority: store-side slice (set by the gateway on return) wins over
        // the initial config; otherwise fall back to initial config.
        const fromStore = insightAlertStore.lightningSelection;
        const sliceToHydrate =
          fromStore ?? initialSensorAlertConfig?.lightning ?? null;
        if (sliceToHydrate) {
          lightning.hydrate(sliceToHydrate);
          // Mirror to formik so save-on-mount picks it up.
          persistLightningSlice(sliceToHydrate);
          setHasHydratedLightning(true);
          // Default to the Raio tab when lightning is the only configured
          // slice, OR when the user just placed lightning via the gateway.
          const hasInitialRain =
            (initialSensorAlertConfig?.sensors?.length ?? 0) > 0;
          const justPlacedViaGateway =
            !!fromStore && !initialSensorAlertConfig?.lightning;
          if (justPlacedViaGateway || !hasInitialRain) {
            setActiveTab("lightning");
          }
        } else if (initialSensorAlertConfig !== undefined) {
          setHasHydratedLightning(true);
        }
      }, [
        hasHydratedLightning,
        initialSensorAlertConfig,
        insightAlertStore,
        lightning,
        lightningAllowed,
        persistLightningSlice,
      ]);

      const handleLightningChange = useCallback(
        (next: ISensorAlertConfigLightning) => {
          lightning.setSlice(next);
          persistLightningSlice(next);
        },
        [lightning, persistLightningSlice],
      );

      // Extra query params appended when in edit mode so the return URL carries
      // enough context to reopen the correct edit drawer.
      const editOriginSuffix = editModeAlertId
        ? `&alert_id=${editModeAlertId}`
        : editModeEventId
          ? `&event_insight_id=${editModeEventId}`
          : "";

      // Load sensors on mount (no macroRegionId filter for now)
      useEffect(() => {
        if (macroRegionId || macroRegionOrigin) {
          insightAlertStore.loadSensors();
        }
      }, [macroRegionId, macroRegionOrigin, insightAlertStore]);

      // Load sensor-specific environmental variables (1h–120h precipitation accumulations)
      useEffect(() => {
        if (macroRegionOrigin) {
          insightAlertStore.loadSensorEnvironmentalVariables(macroRegionOrigin);
        }
      }, [macroRegionOrigin, insightAlertStore]);

      const sensors = insightAlertStore.sensorMeasuredData;
      const isLoading =
        insightAlertStore.sensorDataState === StoreState.PEDDING;
      const hasError = insightAlertStore.sensorDataState === StoreState.ERROR;
      const selectedSensorIds = insightAlertStore.selectedSensorIds;

      const selectedSensorsList = useMemo(() => {
        const idSet = new Set(selectedSensorIds);
        return sensors.filter((s) => idSet.has(s.station_id));
      }, [sensors, selectedSensorIds]);

      const formattedConditions = useMemo(
        () =>
          formatConditions(
            sensorConditions,
            i18n.language === "en"
              ? "en"
              : i18n.language === "es"
              ? "es"
              : "pt-BR",
          ),
        [sensorConditions, i18n.language],
      );

      // Calculate suggestion
      const { suggestedSensors, radiusKm } = useSensorSuggestion({
        locationPoint,
        geofence,
        sensors,
      });

      const hasSuggestion = suggestedSensors.length > 0;

      // Manage section state
      const handleSelectSensors = useCallback(
        (ids: number[]) => {
          insightAlertStore.setSelectedSensorIds(ids);

          // Spec 043 — preserve any existing lightning slice when persisting
          // a rain selection change. The hook's hydration effect runs in the
          // same render as the rain-sync effect; the local `lightning.slice`
          // and `lightningSliceRef` may still be null at first call. Fall
          // back to `insightAlertStore.lightningSelection` (canonical store
          // populated by the gateway) and finally to the initial config so
          // the lightning row never gets dropped from the payload.
          const lightningToPreserve =
            lightning.slice ??
            insightAlertStore.lightningSelection ??
            initialSensorAlertConfig?.lightning ??
            undefined;

          if (ids.length > 0) {
            // Build sensor selections for form (Map for O(1) lookups)
            const sensorMap = new Map(sensors.map((s) => [s.station_id, s]));
            const sensorSelections = ids
              .map((id) => {
                const sensor = sensorMap.get(id);
                if (!sensor) return null;
                return {
                  stationId: sensor.station_id,
                  regionId: sensor.region_id,
                  stationName: sensor.station_name,
                };
              })
              .filter(Boolean);

            setFieldValue("sensorAlertConfig", {
              sensors: sensorSelections,
              conditions: sensorConditions?.length > 0 ? sensorConditions : [],
              alertFrequency: alertFrequency ?? 60,
              lightning: lightningToPreserve,
            });
          } else {
            // Explicitly send empty config to trigger backend deletion (FR-022a).
            // Lightning slice (if any) survives — it lives on its own row.
            setFieldValue("sensorAlertConfig", {
              sensors: [],
              conditions: [],
              lightning: lightningToPreserve,
            });
          }
        },
        [
          insightAlertStore,
          sensors,
          setFieldValue,
          sensorConditions,
          alertFrequency,
          lightning.slice,
          initialSensorAlertConfig,
        ],
      );

      const { state } = useSensorAlertSection({
        sensors,
        suggestedSensors,
        isLoading,
        hasError,
        selectedSensorIds,
        onSelectSensors: handleSelectSensors,
        initialSensorAlertConfig,
      });

      const handleRetry = useCallback(() => {
        insightAlertStore.loadSensors();
      }, [insightAlertStore]);

      const handleViewNearbySensors = useCallback(() => {
        // Save current selection snapshot so we can restore on back/cancel
        forecastChartPageStore.setPreSensorSelectionIds([...selectedSensorIds]);
        // Navigate to Forecast Charts with suggested sensors pre-selected (FR-011)
        const ids = suggestedSensors.map((s) => s.station_id);
        insightAlertStore.setSelectedSensorIds(ids);
        history.push(
          `/forecast-charts?sensor_selection=true&origin=${sensorSelectionOrigin}&suggestion_review=true${editOriginSuffix}`,
        );
      }, [
        history,
        insightAlertStore,
        forecastChartPageStore,
        suggestedSensors,
        selectedSensorIds,
        sensorSelectionOrigin,
        editOriginSuffix,
      ]);

      const handleCreateMyOwnWay = useCallback(() => {
        // Save current selection snapshot so we can restore on back/cancel
        forecastChartPageStore.setPreSensorSelectionIds([...selectedSensorIds]);
        insightAlertStore.clearSensorSelection();
        history.push(
          `/forecast-charts?sensor_selection=true&origin=${sensorSelectionOrigin}${editOriginSuffix}`,
        );
      }, [
        history,
        insightAlertStore,
        forecastChartPageStore,
        selectedSensorIds,
        sensorSelectionOrigin,
        editOriginSuffix,
      ]);

      const handleEditSelection = useCallback(() => {
        // Save current selection snapshot so we can restore on back/cancel
        forecastChartPageStore.setPreSensorSelectionIds([...selectedSensorIds]);
        insightAlertStore.setSelectedSensorIds(selectedSensorIds);
        // Spec 043 — snapshot lightning slice + push it into canonical store
        // so the gateway pre-loads the placed point on the Raio tab. Both
        // Chuva and Raio tabs are always available in the gateway (the save
        // flow routes through PUT /event/:id which handles multi-row reconcile).
        forecastChartPageStore.setPreSensorSelectionLightning(
          lightning.slice ?? null,
        );
        insightAlertStore.setLightningSelection(lightning.slice ?? null);
        // Land the gateway on the same tab the user came from. Without this
        // the map always opens on "rain" (store default) and a lightning-only
        // edit would render an empty rain pane until the user manually
        // switches tabs — the saved point only renders on the Raio tab.
        forecastChartPageStore.setSensorSelectionTab(
          coerceSensorAlertTab(
            activeTab,
            selectedSensorIds.length > 0,
            !!lightning.slice,
          ),
        );
        history.push(
          `/forecast-charts?sensor_selection=true&origin=${sensorSelectionOrigin}${editOriginSuffix}`,
        );
      }, [
        history,
        insightAlertStore,
        forecastChartPageStore,
        selectedSensorIds,
        sensorSelectionOrigin,
        editOriginSuffix,
        lightning.slice,
        activeTab,
      ]);

      const handleDisableConfirm = useCallback(() => {
        setIsDisableModalOpen(false);
        insightAlertStore.clearSensorSelection();
        lightning.clear();
        // Mirror lightning to the store too — without this, dismissing the
        // drawer pre-save and reopening it would re-hydrate the slice from
        // `insightAlertStore.lightningSelection` and undo the disable.
        insightAlertStore.setLightningSelection(null);
        // Empty config triggers backend cascade delete (FR-022a + spec 043 §FR-014).
        setFieldValue("sensorAlertConfig", {
          sensors: [],
          conditions: [],
        });
      }, [insightAlertStore, lightning, setFieldValue]);

      // Spec 043 — per-tab remove. Drops a single sub-block while preserving
      // the other one in the form payload, so PUT /event/:id can reconcile to
      // the surviving slice (see useEventCreate.buildPayload + Aurora parity).
      const handleRequestRemove = useCallback((tab: SensorAlertTabType) => {
        setRemoveTarget(tab);
      }, []);

      const handleRemoveConfirm = useCallback(() => {
        if (removeTarget === "rain") {
          insightAlertStore.clearSensorSelection();
          // Preserve any lightning slice — the section stays in CONFIGURATION
          // and the user keeps editing the surviving Raio sub-block.
          //
          // Same fallback chain as `handleSelectSensors`: right after a
          // gateway round-trip the local hook hasn't hydrated yet, but the
          // canonical store + initial config still hold the slice. Reading
          // only from `lightning.slice` would silently drop the lightning
          // row from the payload in that window.
          const lightningToPreserve =
            lightning.slice ??
            insightAlertStore.lightningSelection ??
            initialSensorAlertConfig?.lightning ??
            undefined;
          setFieldValue("sensorAlertConfig", {
            sensors: [],
            conditions: [],
            lightning: lightningToPreserve,
          });
          // If lightning remains, focus its tab so the body doesn't go blank.
          if (lightningToPreserve) setActiveTab("lightning");
        } else if (removeTarget === "lightning") {
          lightning.clear();
          // Local state alone isn't enough — the canonical store also holds
          // the slice (set by the gateway / persistLightningSlice). Without
          // clearing it here the next drawer mount's hydration effect would
          // read it back and re-create the slice we just removed.
          insightAlertStore.setLightningSelection(null);
          // Build a fresh config that drops `lightning` while preserving the
          // current rain selection + conditions. We don't reach into the form
          // values here — the canonical rain state lives in the store +
          // `sensorConditions` / `alertFrequency` props.
          if (selectedSensorIds.length > 0) {
            const sensorMap = new Map(sensors.map((s) => [s.station_id, s]));
            const sensorSelections = selectedSensorIds
              .map((id) => {
                const sensor = sensorMap.get(id);
                if (!sensor) return null;
                return {
                  stationId: sensor.station_id,
                  regionId: sensor.region_id,
                  stationName: sensor.station_name,
                };
              })
              .filter(Boolean);
            setFieldValue("sensorAlertConfig", {
              sensors: sensorSelections,
              conditions:
                sensorConditions?.length > 0 ? sensorConditions : [],
              alertFrequency: alertFrequency ?? 60,
            });
            setActiveTab("rain");
          } else {
            // No surviving slice — equivalent to a full disable.
            setFieldValue("sensorAlertConfig", { sensors: [], conditions: [] });
          }
        }
        setRemoveTarget(null);
      }, [
        removeTarget,
        insightAlertStore,
        lightning,
        initialSensorAlertConfig,
        selectedSensorIds,
        sensors,
        sensorConditions,
        alertFrequency,
        setFieldValue,
      ]);

      // The section is "in configuration" whenever ANY slice is configured —
      // rain sensors selected OR a lightning slice present. Drives both the
      // header (Ver mensagem / Desativar alerta) and the body (gateway entry
      // + tab strip). Derived once to keep the two callsites in sync.
      const isInConfigurationMode =
        state === SensorAlertState.CONFIGURATION || !!lightning.slice;

      // Per-slice presence drives which tabs render. A tab only appears once
      // its slice is actually configured — adding a missing slice goes
      // through the "Editar seleção de sensores" gateway. Matches Aurora.
      const hasRainSlice = selectedSensorIds.length > 0;
      const hasLightningSlice = !!lightning.slice;

      // Coerce the active tab to a tab that's currently visible — otherwise
      // the body would render an empty pane while the strip filters its
      // sibling tab out (e.g. activeTab="rain" while only Raio is shown).
      // Quando lightning está bloqueado pelo gate de origem, força "rain"
      // para evitar que um slice persistido renderize o LightningTabContent
      // sem aba visível.
      const effectiveActiveTab = !lightningAllowed
        ? "rain"
        : coerceSensorAlertTab(activeTab, hasRainSlice, hasLightningSlice);

      // Don't render if macro region is not determined
      if (!macroRegionId && !macroRegionOrigin) {
        return null;
      }

      return (
        <Box
          id="sensor-alert-section"
          border="1px solid #D4D4D8"
          borderRadius="8px"
          padding="16px"
          position="relative"
          overflow="hidden"
        >
          {/* Blue tab indicator */}
          <Box
            position="absolute"
            top="0"
            left="15px"
            w="29px"
            h="39px"
            bg="#0884CC"
            borderBottomLeftRadius="6px"
            borderBottomRightRadius="6px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Radio size={20} color="white" />
          </Box>

          {/* Header */}
          <Flex
            align="center"
            justify="space-between"
            mb={state === SensorAlertState.EMPTY ? "0" : "16px"}
          >
            <Flex align="center" gap="25px">
              {/* Spacer for the absolute-positioned blue tab */}
              <Box w="20px" />
              <Flex align="center" gap="16px">
                <Text
                  fontWeight="400"
                  fontSize="16px"
                  lineHeight="24px"
                  color="#086AA9"
                >
                  {t("includeTitle")}
                </Text>
                <Box
                  bg="#F7F0FD"
                  color="#7F17DE"
                  px="12px"
                  py="2px"
                  borderRadius="4px"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  whiteSpace="nowrap"
                  flexShrink={0}
                  h="24px"
                  display="flex"
                  alignItems="center"
                >
                  {t("newBadge")}
                </Box>
              </Flex>
            </Flex>

            {/* Right side: action buttons in CONFIGURATION, "Como funciona" otherwise */}
            {isInConfigurationMode ? (
              <Flex align="center" gap="8px">
                {/* View message button (FR-021) */}
                <Button
                  variant="link"
                  size="sm"
                  color="#0884CC"
                  fontWeight="500"
                  lineHeight="1.5em"
                  leftIcon={<Eye size={16} />}
                  onClick={() => setIsMessageModalOpen(true)}
                >
                  {t("viewMessage")}
                </Button>
                {/* Disable alert button (FR-022) */}
                <Flex
                  as="button"
                  type="button"
                  align="center"
                  gap="4px"
                  bg="#DBF1FE"
                  color="#0884CC"
                  borderRadius="6px"
                  fontWeight="500"
                  fontSize="12px"
                  lineHeight="1.5em"
                  h="28px"
                  px="16px"
                  py="4px"
                  cursor="pointer"
                  _hover={{ opacity: 0.85 }}
                  onClick={() => setIsDisableModalOpen(true)}
                >
                  <X size={16} />
                  <Text as="span" whiteSpace="nowrap">
                    {t("disableAlert")}
                  </Text>
                </Flex>
              </Flex>
            ) : (
              <Flex
                as="button"
                type="button"
                align="center"
                gap="4px"
                cursor="pointer"
                borderRadius="6px"
                onClick={() => setIsHowItWorksOpen(true)}
              >
                <HelpCircle size={16} color="#0884CC" />
                <Text fontSize="14px" fontWeight="500" color="#0884CC">
                  {t("howItWorks")}
                </Text>
              </Flex>
            )}
          </Flex>

          {/* Content based on state */}
          {isLoading ? (
            <Flex align="center" justify="center" py="32px">
              <Spinner size="md" color="#086AA9" />
            </Flex>
          ) : hasError ? (
            <Flex direction="column" align="center" gap="8px" py="16px">
              <Text fontSize="14px" color="#BF1F1F" textAlign="center">
                {t("errorLoadingSensors")}
              </Text>
              <Button
                variant="outline"
                size="sm"
                borderColor="#086AA9"
                color="#086AA9"
                onClick={handleRetry}
              >
                {t("retry")}
              </Button>
            </Flex>
          ) : isInConfigurationMode ? (
            <>
              {/* Spec 043 — gateway entry. Visible whenever any slice is
                  configured so the user can switch sensors (Chuva) or
                  reposition the lightning point (Raio) on the map. Visual
                  matches Figma node 3575:3556 — semi-transparent primary
                  fill + primary border (alpha-primary/200 over primary/300). */}
              <Flex
                as="button"
                type="button"
                onClick={handleEditSelection}
                w="100%"
                h="44px"
                align="center"
                justify="space-between"
                bg={SA_COLORS.primarySubtleBg}
                border="1px solid"
                borderColor={SA_COLORS.primaryBorder}
                color={SA_COLORS.primary}
                borderRadius="8px"
                pl="15px"
                pr="8px"
                py="6px"
                mb="16px"
                fontSize="14px"
                fontWeight="500"
                lineHeight="1.5em"
                _hover={{ bg: SA_COLORS.primarySubtleBgHover }}
              >
                <Box>{t("editSensors")}</Box>
                <Box as={ChevronRight as any} boxSize="20px" />
              </Flex>

              {/* Spec 043 — tab strip. Each tab renders iff its slice is
                  configured; the gateway button above is the single entry
                  point for adding a missing slice. Matches Aurora behaviour. */}
              <Box mb="16px">
                <SensorAlertTabs
                  activeTab={effectiveActiveTab}
                  onTabChange={setActiveTab}
                  hasRain={hasRainSlice}
                  hasLightning={hasLightningSlice}
                  rainSensorCount={selectedSensorsList.length}
                  onRemove={handleRequestRemove}
                  hiddenTabs={hiddenSensorTabs}
                />
              </Box>
              {effectiveActiveTab === "lightning" && lightning.slice ? (
                <LightningTabContent
                  slice={lightning.slice}
                  onChange={handleLightningChange}
                />
              ) : (
                <SensorConfiguration
                  t={t}
                  sensorConditions={sensorConditions}
                  alertFrequency={alertFrequency}
                  environmentalVariables={
                    insightAlertStore.sensorEnvironmentalVariables
                  }
                  isLoadingEnvVariables={
                    insightAlertStore.isLoadingSensorEnvironmentalVariables
                  }
                  hasErrorLoadingEnvVariables={
                    insightAlertStore.hasErrorLoadingSensorEnvironmentalVariables
                  }
                  onRetryLoadEnvVariables={() =>
                    macroRegionOrigin &&
                    insightAlertStore.loadSensorEnvironmentalVariables(macroRegionOrigin)
                  }
                  setFieldValue={setFieldValue}
                  selectedSensors={selectedSensorsList}
                  errors={errors}
                  touched={touched}
                />
              )}
            </>
          ) : state === SensorAlertState.EMPTY ? (
            <SensorEmptyState t={t} />
          ) : state === SensorAlertState.INITIAL ? (
            <SensorSuggestion
              t={t}
              totalSensors={sensors.length}
              suggestedSensors={suggestedSensors}
              radiusKm={radiusKm}
              hasSuggestion={hasSuggestion}
              onViewNearbySensors={handleViewNearbySensors}
              onCreateMyOwnWay={handleCreateMyOwnWay}
            />
          ) : null}

          {/* "View message" alert preview modal (FR-021) */}
          <SensorAlertTemplateModal
            isOpen={isMessageModalOpen}
            onClose={() => setIsMessageModalOpen(false)}
            sensorName={selectedSensorsList[0]?.station_name}
            conditions={formattedConditions}
            totalSensors={selectedSensorIds.length}
            eventName={eventName}
            eventLocationName={eventLocationName}
          />

          {/* "How it works" informational modal (FR-023) */}
          <Modal
            isOpen={isHowItWorksOpen}
            onClose={() => setIsHowItWorksOpen(false)}
            isCentered
            size="md"
          >
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent
              borderRadius="8px"
              border="1px solid #E4E4E7"
              position="relative"
            >
              {/* Custom close button — dark circle, top-right (Figma node 2938-14431) */}
              <IconButton
                aria-label={t("closeAriaLabel")}
                icon={<X size={16} />}
                variant="ghost"
                size="sm"
                position="absolute"
                top="7px"
                right="16px"
                minW="29px"
                h="29px"
                borderRadius="30px"
                bg="rgba(0, 0, 0, 0.5)"
                color="white"
                _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
                onClick={() => setIsHowItWorksOpen(false)}
              />
              <ModalBody p="16px" display="flex" flexDir="column" gap="32px">
                {/* Header row: title + "Como funciona?" badge */}
                <Flex gap="16px" align="center" pr="40px">
                  <Text
                    fontSize="20px"
                    fontWeight="600"
                    color="#484848"
                    lineHeight="24px"
                  >
                    {t("howItWorksModalTitle")}
                  </Text>
                  <Box
                    bg="#DBF1FE"
                    color="#0884CC"
                    px="8px"
                    py="2px"
                    borderRadius="4px"
                    fontSize="14px"
                    lineHeight="20px"
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    {t("howItWorks")}?
                  </Box>
                </Flex>

                {/* Body */}
                <Flex flexDir="column" gap="20px">
                  <Text
                    fontSize="16px"
                    color="#484848"
                    lineHeight="20px"
                    fontWeight="300"
                  >
                    {t("howItWorksBody1")}
                  </Text>
                  <Text fontSize="16px" color="#484848" lineHeight="20px">
                    {t("howItWorksBody2Normal")}{" "}
                    <Text as="strong" fontWeight="700">
                      {t("howItWorksBody2Bold")}
                    </Text>
                  </Text>
                </Flex>

                {/* Example message template (Figma node 3183:1873) */}
                <HowItWorksExampleMessage />
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Disable alert confirmation modal (FR-022) */}
          <ConfirmationModal
            isOpen={isDisableModalOpen}
            onClose={() => setIsDisableModalOpen(false)}
            onConfirm={handleDisableConfirm}
            title={t("disableAlertModalTitle")}
            description={t("disableAlertModalDescription")}
            cancelLabel={t("disableAlertCancel")}
            confirmLabel={t("disableAlertConfirm")}
            closeAriaLabel={t("closeAriaLabel") ?? undefined}
          />

          {/* Per-tab remove confirmation modal (spec 043 — Aurora parity) */}
          <ConfirmationModal
            isOpen={removeTarget !== null}
            onClose={() => setRemoveTarget(null)}
            onConfirm={handleRemoveConfirm}
            title={
              removeTarget === "rain"
                ? t("removeRainAlertConfirmTitle")
                : t("removeLightningAlertConfirmTitle")
            }
            description={
              removeTarget === "rain"
                ? t("removeRainAlertConfirmDescription")
                : t("removeLightningAlertConfirmDescription")
            }
            cancelLabel={t("disableAlertCancel")}
            confirmLabel={t("removeAlertConfirmButton")}
            closeAriaLabel={t("closeAriaLabel") ?? undefined}
          />
        </Box>
      );
    },
  );
